/**
 * The `authCode` field of an AUTH_CODE_RECEIVED message carries either a bare base64 code minted
 * by hyrox-api or, once REACT_APP_ONE_FIIT_AUTH_CODE_URL is set, a base64 `{ code, verifier }`
 * payload from one-fiit — the PKCE verifier travels with the code so the Performance Hub relay,
 * which only passes the field through, needs no change.
 *
 * Both ends of that relay can be running different builds mid-rollout, so the format is detected
 * on decode rather than assumed. Encode and decode live together to keep them in step.
 */

export type AuthCode = {
  code: string;
  codeVerifier?: string;
};

export const encodeAuthCode = ({ code, codeVerifier }: AuthCode): string =>
  codeVerifier ? btoa(JSON.stringify({ code, verifier: codeVerifier })) : btoa(code);

export const decodeAuthCode = (authCode: string): AuthCode | null => {
  let decoded: string;
  try {
    decoded = atob(authCode);
  } catch (error) {
    // A relay that truncates or reformats the field lands here — worth surfacing, since the
    // JSON payload is several times longer than a legacy code.
    console.error('Auth code could not be decoded:', error);
    return null;
  }

  try {
    const parsed = JSON.parse(decoded);

    if (parsed && typeof parsed === 'object' && parsed.code) {
      return { code: parsed.code, codeVerifier: parsed.verifier };
    }
  } catch {
    // Legacy codes are opaque strings, not JSON.
  }

  return { code: decoded };
};
